const FORMATTED_TEXT_FIELDS = new Set([
  'description',
  'aboutCompany',
  'companyDescription',
  'socialPackageText',
  'relocationAssistance',
  'skillGroups',
  'aiRequirementsWeighted'
]);

const TEXTUAL_HEX_ESCAPE_PATTERN = /\\x([0-9a-fA-F]{2})/g;

export function sanitizeControlCharactersDeep<T>(value: T, rootPath = 'value'): T {
  return sanitizeUnknown(value, rootPath) as T;
}

export function stripForbiddenControlCharacters(value: string, path = 'value'): string {
  const withoutTextualEscapes = stripForbiddenTextualControlEscapes(value);
  let result = '';

  for (const char of withoutTextualEscapes) {
    if (!isForbiddenControlCharacter(char, path)) {
      result += char;
    }
  }

  return result;
}

export function hasForbiddenControlCharacters(value: string, path = 'value'): boolean {
  if (hasForbiddenTextualControlEscapes(value)) {
    return true;
  }

  for (const char of value) {
    if (isForbiddenControlCharacter(char, path)) {
      return true;
    }
  }

  return false;
}

function stripForbiddenTextualControlEscapes(value: string): string {
  return value.replace(TEXTUAL_HEX_ESCAPE_PATTERN, (match, hex: string) => (
    isControlCode(Number.parseInt(hex, 16)) ? '' : match
  ));
}

function hasForbiddenTextualControlEscapes(value: string): boolean {
  return stripForbiddenTextualControlEscapes(value) !== value;
}

function sanitizeUnknown(value: unknown, path: string): unknown {
  if (typeof value === 'string') {
    return stripForbiddenControlCharacters(value, path);
  }

  if (Array.isArray(value)) {
    return value.map((item, index) => sanitizeUnknown(item, `${path}[${index}]`));
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [
        key,
        sanitizeUnknown(child, `${path}.${key}`)
      ])
    );
  }

  return value;
}

function isForbiddenControlCharacter(char: string, path: string): boolean {
  const code = char.charCodeAt(0);

  if (!isControlCode(code)) {
    return false;
  }

  if ((char === '\t' || char === '\n' || char === '\r')
    && FORMATTED_TEXT_FIELDS.has(lastPropertyName(path))) {
    return false;
  }

  return true;
}

function isControlCode(code: number): boolean {
  return (code >= 0x00 && code <= 0x1F) || (code >= 0x7F && code <= 0x9F);
}

function lastPropertyName(path: string): string {
  const withoutIndex = path.replace(/\[[^\]]*]$/, '');
  const dotIndex = withoutIndex.lastIndexOf('.');
  return dotIndex >= 0 ? withoutIndex.slice(dotIndex + 1) : withoutIndex;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}
